// useRealtime — subscribe to the WHP live-push WebSocket (P3 / WEL-16).
//
// Fetches a short-lived ticket from GET /api/ws/ticket (Keycloak-authed — the
// ticket rides the WS query string because browsers can't set handshake
// headers), opens the API Gateway WebSocket, and calls `onMessage` for each
// pushed update. Reconnects with exponential backoff, minting a fresh ticket
// each attempt. If the server reports no `url` (push not yet enabled), it stops
// quietly — the page's normal load/refresh still works.
//
// Only while someone is using the tab (Aurora load workstream #4). API Gateway
// drops an idle socket every ~10 min, and each re-open is a ticket + a $connect
// INSERT + a $disconnect DELETE on consumer_whp.ws_connections + the page's
// rehydrate refetch — all night long for a tab left open. So while the tab is
// inactive (15 min without input, or hidden for 5 min — the grace keeps a quick
// look at another tab from churning the socket) the socket is closed and NOT
// re-opened. On return it reconnects, and because that open is a re-open (or
// the mount itself happened while inactive) the consumer gets WS_RECONNECT and
// rehydrates — gap recovery is unchanged. Genuine failures while active keep
// the exponential backoff.

import { useEffect, useRef } from 'react';
import { api } from '@/lib/api';
import { getActivityGate, type ActivityWatch } from '@/lib/activityGate';

/** When the socket is allowed to live: 15 min without input, or 5 min hidden, closes it. */
export const REALTIME_ACTIVITY: ActivityWatch = {
  idleMs: 15 * 60_000,
  hiddenGraceMs: 5 * 60_000,
};

interface WsTicket {
  ticket: string;
  url: string | null;
}

/**
 * Sentinel delivered to `onMessage` when the socket RE-opens after a drop (NOT
 * on the first connect). Pushes are missed while disconnected, so on reconnect
 * the consumer should rehydrate (refetch) to recover the gap (design §8 "gap
 * recovery"). Leading `__` so it can't collide with a server message type.
 */
export const WS_RECONNECT = { type: '__ws_reconnect' } as const;

export function useRealtime(onMessage: (msg: unknown) => void): void {
  const cb = useRef(onMessage);
  cb.current = onMessage;

  useEffect(() => {
    const gate = getActivityGate();
    let ws: WebSocket | null = null;
    let stopped = false;
    let pushDisabled = false;
    let connecting = false;
    let attempt = 0;
    let openedOnce = false;
    // True once we deliberately went without a socket (mounted while inactive,
    // or closed it on going inactive): the next open must rehydrate even if it
    // is technically the first.
    let missedWhileInactive = false;
    let timer: number | undefined;

    const active = () => gate.isActive(REALTIME_ACTIVITY);

    const clearTimer = () => {
      if (timer !== undefined) window.clearTimeout(timer);
      timer = undefined;
    };

    const scheduleReconnect = () => {
      // Inactive: stay closed — the gate's resume transition reconnects.
      if (stopped || pushDisabled || !active()) return;
      clearTimer();
      const delay = Math.min(30_000, 1_000 * 2 ** attempt);
      attempt += 1;
      timer = window.setTimeout(() => {
        timer = undefined;
        void connect();
      }, delay);
    };

    const connect = async () => {
      if (stopped || pushDisabled || connecting || ws || !active()) return;
      connecting = true;
      let t: WsTicket;
      try {
        t = await api.get<WsTicket>('/api/ws/ticket');
      } catch {
        connecting = false;
        scheduleReconnect();
        return;
      }
      connecting = false;
      if (stopped) return;
      if (!t.url) {
        pushDisabled = true; // push disabled server-side — don't retry
        return;
      }
      // Went inactive while the ticket was in flight: don't open; the resume
      // mints a fresh ticket.
      if (!active()) return;
      let sock: WebSocket;
      try {
        sock = new WebSocket(`${t.url}?ticket=${encodeURIComponent(t.ticket)}`);
      } catch {
        scheduleReconnect();
        return;
      }
      ws = sock;
      sock.onopen = () => {
        attempt = 0;
        // Gap recovery (§8): a RE-open means we were disconnected and may have
        // missed pushes — tell the consumer to rehydrate. Not on the first open
        // (the initial load already has fresh data) unless that first open was
        // deferred by inactivity.
        if (openedOnce || missedWhileInactive) cb.current(WS_RECONNECT);
        openedOnce = true;
        missedWhileInactive = false;
      };
      sock.onmessage = (ev) => {
        let parsed: unknown = ev.data;
        try {
          parsed = JSON.parse(ev.data as string);
        } catch {
          /* keep the raw value */
        }
        cb.current(parsed);
      };
      sock.onclose = () => {
        if (ws === sock) ws = null;
        scheduleReconnect();
      };
      sock.onerror = () => {
        sock.close();
      };
    };

    // Deliberate close: detach the handlers first so the close doesn't schedule
    // a reconnect.
    const disconnect = () => {
      clearTimer();
      const sock = ws;
      ws = null;
      if (!sock) return;
      sock.onopen = null;
      sock.onmessage = null;
      sock.onclose = null;
      sock.onerror = null;
      sock.close();
    };

    const unsubscribe = gate.subscribe((isActive) => {
      if (stopped) return;
      if (isActive) {
        void connect();
      } else {
        missedWhileInactive = true;
        disconnect();
      }
    }, REALTIME_ACTIVITY);

    if (active()) void connect();
    else missedWhileInactive = true;
    return () => {
      stopped = true;
      unsubscribe();
      disconnect();
    };
  }, []);
}
